import React, { Suspense, useEffect, useRef, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import Portal from '../../../../utils/portal/Portal';
import { withAreaGetter } from '../../../../providers/AreasConsumers';
import { areas } from '../../../../constants/areas';
import PartnerFileSwitcher from './PartnerFileSwitcher';
import { loadDocumentRights } from 'actions/actionCreators/documentRightsActions';
import { showInfo } from 'actions/actionCreators/snackbarActions';
import { resetCurrentOffer } from 'actions/actionCreators/offer/offerActions';
import { RightsContext } from '../../../../providers/RightsContext';
import { getDocumentRightsByStateKey } from 'constants/common';
import { useSubscription } from '@apollo/client';
import { DOCUMENT_UPDATED } from 'features/document/edit/subscriptions';
import { selectUserId } from 'selectors/userInfoSelectors';
import { setContent, showRightSideBar } from 'components/layout/RightSideBar';
import DocumentComments from 'features/offer/edit/sidebar/components/comments/DocumentComments';
import PartnerFileHeader from './PartnerFileHeader';
import WrappedFakeText from 'components/common/WrappedFakeText';
import { useTitle } from 'utils/hooks/useTitle';

const PartnerFileWizard = ({ documentId, siteId, isNewPartnerFile, isNewBulkFile, partnerFileHeader, history, getElemByAreaKey }) => {
  const { t } = useTranslation();
  const mounted = useRef(false);
  const dispatch = useDispatch();

  const rightsStateKey = getDocumentRightsByStateKey(partnerFileHeader?.documentStateId, partnerFileHeader?.documentTypeId);
  const rights = useSelector(state => state.documentRights[rightsStateKey] || {}, shallowEqual);
  const userId = useSelector(state => selectUserId(state));
  const [changeIndex, setChangeIndex] = useState(0);

  const setSidebarContent = useCallback(() => {
    setContent(
      <RightsContext.Provider value={rights}>
        <DocumentComments documentId={documentId} siteId={siteId} history={history} fullScreen={false} />
      </RightsContext.Provider>
    );
  }, [documentId, siteId, rights, history]);

  useEffect(() => {
    mounted.current = true;
    if (!isNewPartnerFile && !isNewBulkFile && !mounted.current) return;

    dispatch(loadDocumentRights(documentId, siteId, partnerFileHeader.documentStateId, partnerFileHeader.documentTypeId));
    showRightSideBar(true);

    return () => {
      mounted.current = false;
      dispatch(resetCurrentOffer(documentId, siteId));
      showRightSideBar(false);
    };
  }, [dispatch, documentId, siteId, isNewPartnerFile, isNewBulkFile, partnerFileHeader.documentStateId, partnerFileHeader.documentTypeId]);

  useEffect(() => {
    setSidebarContent();
    if (!mounted.current || rights.isLoaded) return;
    dispatch(loadDocumentRights(documentId, siteId, partnerFileHeader.documentStateId, partnerFileHeader.documentTypeId));
  }, [
    documentId,
    siteId,
    rights.isLoaded,
    partnerFileHeader.documentStateId,
    partnerFileHeader.documentTypeId,
    dispatch,
    setSidebarContent
  ]);

  useTitle(
    isNewPartnerFile || !partnerFileHeader?.documentNumber
      ? t('PartnerFile.NewPartnerFilePageTitle')
      : isNewBulkFile
      ? t('PartnerFile.NewBulkFilePageTitle')
      : t('PartnerFile.PageTitle', { fileNumber: partnerFileHeader.documentNumber })
  );

  useSubscription(DOCUMENT_UPDATED, {
    variables: { documentId, siteId, userId },
    onSubscriptionData: data => {
      if (data) dispatch(showInfo(t('PartnerFile.ModifiedData'), false, true));
    }
  });

  return (
    <RightsContext.Provider value={rights}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={12}>
          <Suspense fallback={<WrappedFakeText onPaper />}>
            <PartnerFileSwitcher history={history} changeIndex={changeIndex} setChangeIndex={setChangeIndex} />
          </Suspense>
        </Grid>
      </Grid>
      <Portal container={getElemByAreaKey(areas.header)}>
        <PartnerFileHeader
          documentId={documentId}
          siteId={siteId}
          fileNumber={partnerFileHeader?.documentNumber}
          changeIndex={changeIndex}
          setChangeIndex={setChangeIndex}
          isNewPartnerFile={isNewPartnerFile}
          isNewBulkFile={isNewBulkFile}
        />
      </Portal>
    </RightsContext.Provider>
  );
};

PartnerFileWizard.propTypes = {
  documentId: PropTypes.number,
  siteId: PropTypes.number,
  isNewPartnerFile: PropTypes.bool,
  isNewBulkFile: PropTypes.bool,
  partnerFileHeader: PropTypes.object,
  history: PropTypes.object.isRequired,
  getElemByAreaKey: PropTypes.func.isRequired
};

export default withAreaGetter(PartnerFileWizard);
